<template>
  <subject-level-and-stream-selection
    class="field-group type-dropdown"
    :subjects="subjectOptions"
    :level-and-streams="levelAndStreamOptions"
    :init-subject-selected="subjectSelectedUI"
    :init-level-selected="levelAndStreamSelectedUI"
    :is-multi-select="false"
    :subject-filter-mode="SUBJECT_FILTER_MODE.ACTIVE_PLUS_SELECTED"
    :show-subject-level-label="false"
    :show-delete-row-button="!isAdaptiveSection"
    :is-level-disabled="isLevelDisabled"
    :selection-kind="selectionKind"
    @selection-update="handleSubjectLevelChange"
    @delete-subject-level="handleDeleteSubjectLevel"
  />
</template>

<script setup lang="ts">
import ResourceTaggingSelectionStore from "@modules/resource-tagging-selection/components/ResourceTaggingSelectionStore";
import ResourceTaggingsStore from "@modules/editor/components/resource-taggings/ResourceTaggingsStore";
import SubjectLevelAndStreamSelection from "@plugins/subject-level-and-stream-selection/components/SubjectLevelAndStreamSelection.vue";
import { isEmpty } from "@utils/var.check";
import {
  FOLLOWED_LEVEL_AND_COURSE_HEADER,
  FOLLOWED_SUBJECTS_HEADER,
  MY_LEVEL_AND_COURSE_HEADER,
  MY_SUBJECTS_HEADER,
  OTHER_LEVEL_AND_COURSE_HEADER,
  OTHER_SUBJECTS_HEADER,
} from "@plugins/subject-level-and-stream-selection/subject.level.and.stream.selection.constant";
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useModal } from "@/plugins/modal/composables/modal-instance";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import PageDirtyMixin from "@modules/app/mixin/page.dirty.mixin";
import RandomUtils from "@utils/random.utils.ts";
import { buildSubjectOptions, SUBJECT_FILTER_MODE } from "@utils/subject.utils.ts";

// Composables
const modal = useModal();
const { t } = useI18n();

// Emits
const emit = defineEmits([
  "update:taggingSelection",
  "ChangeSelection",
  "UpdateTagging",
  "subjectSelected",
  "deleteRequiredContentMap",
]);

// Data
const subjectSelectedUI = ref("");
const levelAndStreamSelectedUI = ref("");

const taggingStore = ref(ResourceTaggingSelectionStore);
const resourceTaggingsStore = ref(ResourceTaggingsStore);

const { setDirty } = useDirty(ref(""));

// Props
interface Props {
  resourceUuid: string; // required
  subjectLevelTagging?: Record<string, unknown>;
  isAdaptiveSection?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  subjectLevelTagging: () => ({}),
  isAdaptiveSection: false,
});

//Mixins
defineOptions({
  mixins: [PageDirtyMixin],
});

// Computed
const processingId = computed(() => {
  return props.resourceUuid;
});

const subjectLevelTaggings = computed(() => {
  return resourceTaggingsStore.value.getSubjectAndLevelTaggingsByResource(props.resourceUuid);
});

const isLevelDisabled = computed(() => {
  return levelAndStreamOptions.value.length === 0;
});

const mySubjects = computed(() => {
  return taggingStore.value.getSubjectsByType("MINE");
});

const followedSubjects = computed(() => {
  return taggingStore.value.getSubjectsByType("FOLLOWED");
});

const otherSubjects = computed(() => {
  return taggingStore.value.getSubjectsByType("OTHER");
});

const contentMapTaggings = computed(() => {
  return resourceTaggingsStore.value.getContentMapTaggingsByResource(props.resourceUuid);
});

const subjectOptions = computed(() => {
  const isShowSubject = (subject, taggedLevelIds) => {
    const currentSelectedSubject = props.subjectLevelTagging.subject.id === subject.subjectId;

    const subjectMetadata = taggingStore.value.getSubject(subject.subjectId.toString());
    const allLevelSelected =
      taggedLevelIds &&
      subjectMetadata?.levelAndStreams?.every((level) => taggedLevelIds.includes(level.id));
    return currentSelectedSubject || !allLevelSelected;
  };

  // Create currently tagged Subject To LevelId Map
  const taggedSubjectLevels = subjectLevelTaggings.value;
  const taggedSubjectLevelIds = {};

  taggedSubjectLevels.forEach((taggedSubjectLevel) => {
    if (!isEmpty(taggedSubjectLevel.levelAndStream)) {
      const taggedLevelIds = taggedSubjectLevelIds[taggedSubjectLevel.subject.id] || [];
      taggedLevelIds.push(taggedSubjectLevel.levelAndStream.id);
      taggedSubjectLevelIds[taggedSubjectLevel.subject.id] = taggedLevelIds;
    }
  });

  let subjects = [];
  const newMySubjects = mySubjects.value.filter((i) => {
    return isShowSubject(i, taggedSubjectLevelIds[i.subjectId]);
  });

  if (newMySubjects.length > 0) {
    subjects = [
      {
        subjectName: t(MY_SUBJECTS_HEADER.subjectName),
        subjectId: MY_SUBJECTS_HEADER.subjectId,
      },
      ...newMySubjects,
    ];
  }

  const newFollowedSubjects = followedSubjects.value.filter((i) => {
    return isShowSubject(i, taggedSubjectLevelIds[i.subjectId]);
  });

  if (newFollowedSubjects.length > 0) {
    subjects = [
      ...subjects,
      {
        subjectName: t(FOLLOWED_SUBJECTS_HEADER.subjectName),
        subjectId: FOLLOWED_SUBJECTS_HEADER.subjectId,
      },
      ...newFollowedSubjects,
    ];
  }

  const newOtherSubjects = otherSubjects.value.filter((i) => {
    return isShowSubject(i, taggedSubjectLevelIds[i.subjectId]);
  });

  if (newOtherSubjects.length > 0) {
    subjects = [
      ...subjects,
      {
        subjectName: t(OTHER_SUBJECTS_HEADER.subjectName),
        subjectId: OTHER_SUBJECTS_HEADER.subjectId,
      },
      ...newOtherSubjects,
    ];
  }

  return buildSubjectOptions(subjects);
});

const levelAndStreamOptions = computed(() => {
  if (isEmpty(subjectSelectedUI.value)) {
    return [];
  }

  const selectedSubject = taggingStore.value.getSubject(subjectSelectedUI.value);
  if (!selectedSubject || !selectedSubject.levelAndStreams) {
    return [];
  }

  const isShowLevelAndStream = (level) => {
    const currentlySelectedLevel =
      !isEmpty(levelAndStreamSelectedUI.value) &&
      parseInt(levelAndStreamSelectedUI.value) === level.id;

    const subjectLevels = subjectLevelTaggings.value.filter(
      (tagging) =>
        !isEmpty(tagging.levelAndStream) &&
        tagging.subject.id === parseInt(subjectSelectedUI.value),
    );

    const unselectedLevel = subjectLevels.every(
      (subjectLevel) => level.id !== subjectLevel.levelAndStream.id,
    );

    return currentlySelectedLevel || unselectedLevel;
  };

  const myLevelAndStreams = taggingStore.value
    .getSubjectLevelsByType(subjectSelectedUI.value, "MINE")
    .filter(isShowLevelAndStream);
  const followedLevelAndStreams = taggingStore.value
    .getSubjectLevelsByType(subjectSelectedUI.value, "FOLLOWED")
    .filter(isShowLevelAndStream);
  const otherLevelAndStreams = taggingStore.value
    .getSubjectLevelsByType(subjectSelectedUI.value, "OTHER")
    .filter(isShowLevelAndStream);

  let levelAndStreams = [];

  if (myLevelAndStreams.length > 0) {
    levelAndStreams = [
      {
        displayName: t(MY_LEVEL_AND_COURSE_HEADER.displayName),
        id: MY_LEVEL_AND_COURSE_HEADER.id,
      },
      ...myLevelAndStreams,
    ];
  }

  if (followedLevelAndStreams.length > 0) {
    levelAndStreams = [
      ...levelAndStreams,
      {
        displayName: t(FOLLOWED_LEVEL_AND_COURSE_HEADER.displayName),
        id: FOLLOWED_LEVEL_AND_COURSE_HEADER.id,
      },
      ...followedLevelAndStreams,
    ];
  }

  if (otherLevelAndStreams.length > 0) {
    levelAndStreams = [
      ...levelAndStreams,
      {
        displayName: t(OTHER_LEVEL_AND_COURSE_HEADER.displayName),
        id: OTHER_LEVEL_AND_COURSE_HEADER.id,
      },
      ...otherLevelAndStreams,
    ];
  }

  return levelAndStreams.map((levelAndStream) => {
    return {
      name: levelAndStream.displayName,
      label: levelAndStream.displayName,
      value: levelAndStream.id.toString(),
      header: levelAndStream.id === 0,
    };
  });
});

// Methods
const handleSubjectLevelChange = function (data) {
  if (
    !isEmpty(props.subjectLevelTagging.subject.id) &&
    data.type === "subject" &&
    parseInt(data.data) === props.subjectLevelTagging.subject.id
  ) {
    return;
  }

  if (
    !isEmpty(props.subjectLevelTagging.levelAndStream.id) &&
    data.type === "level" &&
    parseInt(data.data) === props.subjectLevelTagging.levelAndStream.id
  ) {
    return;
  }

  if (
    !isEmpty(props.subjectLevelTagging.subject.id) &&
    !isEmpty(props.subjectLevelTagging.levelAndStream) &&
    !isEmpty(props.subjectLevelTagging.levelAndStream.id) &&
    hasOrphanedContentMap()
  ) {
    showSubjectLevelChangeConfirmationModal().then((response) => {
      if (response) {
        deleteRequiredContentMap();
        changeSubjectLevel(data);
      }
    });
  } else {
    changeSubjectLevel(data);
  }
};

const handleDeleteSubjectLevel = function () {
  if (
    !isEmpty(props.subjectLevelTagging.subject.id) &&
    !isEmpty(props.subjectLevelTagging.levelAndStream) &&
    !isEmpty(props.subjectLevelTagging.levelAndStream.id) &&
    hasOrphanedContentMap()
  ) {
    showSubjectLevelDeleteConfirmationModal().then((response) => {
      if (response) {
        deleteRequiredContentMap();
        handleRemoveSubjectLevelTagging(props.subjectLevelTagging.tagId);
      }
    });
  } else {
    handleRemoveSubjectLevelTagging(props.subjectLevelTagging.tagId);
  }
};

const hasOrphanedContentMap = function () {
  // Orphaned Content Map refers to a content map that is only linked to one (out of x) subjects among the subject-level taggings
  const validContentMapTaggings = contentMapTaggings.value.filter(
    (contentMapTagging) => !isEmpty(contentMapTagging.contentMapMetadata),
  );

  if (isEmpty(validContentMapTaggings)) {
    return false;
  }

  const contentMapsWithDeletingSubjectLevel = validContentMapTaggings.filter(
    (contentMapTagging) => {
      const cmUuid = contentMapTagging.contentMapMetadata.uuid;

      const cmMetadata = taggingStore.value.getContentMap(cmUuid);
      if (isEmpty(cmMetadata)) {
        return false;
      }

      const cmSubject = cmMetadata.subjects[props.subjectLevelTagging.subject.id];
      if (isEmpty(cmSubject)) {
        return false;
      }

      if (isEmpty(props.subjectLevelTagging.levelAndStream)) {
        return false;
      }

      const cmSubjectLevel = cmSubject.levelAndStreams[props.subjectLevelTagging.levelAndStream.id];
      if (isEmpty(cmSubjectLevel)) {
        return false;
      }
      return true;
    },
  );

  const otherSubjectLevelTaggings = subjectLevelTaggings.value.filter(
    (subjectLevelTagging) => subjectLevelTagging.tagId !== props.subjectLevelTagging.tagId,
  );

  if (isEmpty(otherSubjectLevelTaggings)) {
    return !isEmpty(contentMapTaggings.value);
  }

  return contentMapsWithDeletingSubjectLevel.some((contentMap) => {
    const cmUuid = contentMap.contentMapMetadata.uuid;
    const cmMetadata = taggingStore.value.getContentMap(cmUuid);
    return otherSubjectLevelTaggings.every((otherSubjectLevelTagging) => {
      if (
        isEmpty(otherSubjectLevelTagging.subject.id) ||
        isEmpty(otherSubjectLevelTagging.levelAndStream) ||
        isEmpty(otherSubjectLevelTagging.levelAndStream.id)
      ) {
        return true;
      }

      const cmSubject = cmMetadata.subjects[otherSubjectLevelTagging.subject.id];
      if (isEmpty(cmSubject)) {
        return true;
      }
      const cmSubjectLevel = cmSubject.levelAndStreams[otherSubjectLevelTagging.levelAndStream.id];
      if (isEmpty(cmSubjectLevel)) {
        return true;
      }
      return false;
    });
  });
};

const showSubjectLevelChangeConfirmationModal = function () {
  return new Promise((resolve) => {
    const props = {
      title: t("resource_tagging_subject_level_change_confirmation_title"),
      content: t("resource_tagging_subject_level_change_confirmation_message"),
      primaryBtnLabel: t("button_ok"),
      secondaryBtnLabel: t("button_cancel"),
      actionPrimary: () => {
        resolve(true);
      },
      actionSecondary: () => {
        resolve(false);
      },
    };
    modal.showMessage("success", props);
  });
};

const showSubjectLevelDeleteConfirmationModal = function () {
  return new Promise((resolve) => {
    const props = {
      title: t("resource_tagging_subject_level_delete_confirmation_title"),
      content: t("resource_tagging_subject_level_delete_confirmation_message"),
      primaryBtnLabel: t("button_ok"),
      secondaryBtnLabel: t("button_cancel"),
      actionPrimary: () => {
        resolve(true);
      },
      actionSecondary: () => {
        resolve(false);
      },
    };
    modal.showMessage("success", props);
  });
};

const changeSubjectLevel = function (data) {
  const taggingToUpdate = props.subjectLevelTagging;
  if (data.type === "subject") {
    if (isEmpty(data.data)) {
      subjectSelectedUI.value = "";
      taggingToUpdate.subject = {
        id: "",
      };

      levelAndStreamSelectedUI.value = "";
      taggingToUpdate.levelAndStream = {
        id: "",
      };
    } else {
      subjectSelectedUI.value = data.data;
      taggingToUpdate.subject = {
        id: parseInt(data.data),
      };

      emit("subjectSelected", parseInt(data.data));

      const availableOptions = levelAndStreamOptions.value.filter((option) => !option.header);
      if (availableOptions.length > 0) {
        levelAndStreamSelectedUI.value = availableOptions[0].value;
        taggingToUpdate.levelAndStream = {
          id: parseInt(levelAndStreamSelectedUI.value),
        };
      } else {
        // Reset level if no options available yet
        levelAndStreamSelectedUI.value = "";
        taggingToUpdate.levelAndStream = {
          id: "",
        };
      }
    }
  } else if (data.type === "level") {
    levelAndStreamSelectedUI.value = isEmpty(data.data) ? "" : data.data;
    taggingToUpdate.levelAndStream = {
      id: isEmpty(data.data) ? "" : parseInt(data.data),
    };
  }
  setDirty(props.resourceUuid);
  resourceTaggingsStore.value.addSubjectLevelTagging(taggingToUpdate);
};

const handleRemoveSubjectLevelTagging = function (tagId) {
  const editingTaggingLength = !isEmpty(subjectLevelTaggings.value)
    ? subjectLevelTaggings.value.length
    : 0;

  if (!isEmpty(subjectLevelTaggings.value) && editingTaggingLength === 1) {
    addDefaultSubjectLevelTagging();
  }

  setDirty(processingId.value);

  resourceTaggingsStore.value.removeSubjectLevelTagging(tagId);
};

const addDefaultSubjectLevelTagging = function () {
  const defaultSubjectLevelTaggingToAdd = {};
  const tagId = RandomUtils.randomId();

  defaultSubjectLevelTaggingToAdd.tagId = tagId;
  defaultSubjectLevelTaggingToAdd.levelAndStream = { id: "" };
  defaultSubjectLevelTaggingToAdd.subject = { id: "" };
  defaultSubjectLevelTaggingToAdd.contentMapMetadata = null;
  resourceTaggingsStore.value.addSubjectLevelTagging(defaultSubjectLevelTaggingToAdd);
};

const deleteRequiredContentMap = function () {
  emit("deleteRequiredContentMap", props.subjectLevelTagging);
};

// Created
subjectSelectedUI.value = props.subjectLevelTagging.subject.id.toString();
levelAndStreamSelectedUI.value = isEmpty(props.subjectLevelTagging.levelAndStream)
  ? ""
  : props.subjectLevelTagging.levelAndStream.id.toString();
</script>
